import {
  AfterViewInit, Component, ElementRef, HostListener, inject, Input, OnChanges,
  OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface EchartsHeatmapPoint {
  x: number;
  y: number;
  value: number;
}

@Component({
  selector: 'urban-echarts-heatmap',
  templateUrl: './echarts-heatmap.component.html',
  styleUrls: ['./echarts-heatmap.component.scss'],
  standalone: false,
})
export class EchartsHeatmapComponent
  implements AfterViewInit, OnChanges, OnDestroy {

  @Input() public chartTitle: string;
  @Input() public xAxisLabels: string[] = [];
  @Input() public yAxisLabels: string[] = [];
  @Input() public dataPoints: EchartsHeatmapPoint[] = [];
  @Input() public minValue: number = 0;
  @Input() public maxValue: number = 100;
  @Input() public valueSuffix: string = '';
  @Input() public valueLabelHigh: string = 'High';
  @Input() public valueLabelLow: string = 'Low';
  @Input() public darkThemeActive: boolean;
  @Input() public passedHeight: number = 320;

  @ViewChild('heatmapContainer', { static: true })
  private container: ElementRef<HTMLDivElement>;

  private readonly translate = inject(TranslateService);

  private chart: any;
  private echarts: any;
  private translatedTitle: string = '';
  public noDataOnChart: boolean = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  @HostListener('window:resize')
  public onResize(): void {
    if (this.chart) {
      this.chart.resize();
    }
  }

  public async ngAfterViewInit(): Promise<void> {
    if (!this.echarts) {
      const mod: any = await import('echarts');
      this.echarts = mod?.default ?? mod;
    }
    this.translateTitle();

    this.translate.onLangChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.translateTitle());

    this.drawChart();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.echarts) {
      return;
    }
    if (changes['chartTitle']) {
      this.translateTitle();
    }
    this.drawChart();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.chart) {
      this.chart.dispose();
      this.chart = undefined;
    }
  }

  private translateTitle(): void {
    if (!this.chartTitle) {
      this.translatedTitle = '';
      return;
    }
    this.translate.get(this.chartTitle)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((t: string) => {
        this.translatedTitle = t;
        if (this.chart) {
          this.drawChart();
        }
      });
  }

  private drawChart(): void {
    if (!this.echarts || !this.container) {
      return;
    }

    const hasData: boolean = (this.dataPoints?.length ?? 0) > 0
      && this.xAxisLabels?.length > 0
      && this.yAxisLabels?.length > 0;
    this.noDataOnChart = !hasData;

    if (!this.chart) {
      this.chart = this.echarts.init(this.container.nativeElement, null,
        { renderer: 'canvas' });
    }

    if (!hasData) {
      this.chart.clear();
      return;
    }

    const textColor: string = this.darkThemeActive
      ? this.cssVar('--custom-light-primary-text-color', '#F3F9FF')
      : this.cssVar('--custom-dark-primary-text-color', '#151515');
    const splitLineColor: string = this.darkThemeActive
      ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

    const ramp: string[] = this.darkThemeActive
      ? ['#0B1C2D', '#143d70', '#1a528f', '#0971CE', '#2B96F6']
      : ['#E7F3FE', '#A2D1FB', '#5AAEEF', '#0971CE', '#003C92'];

    const series: any[] = [{
      name: this.valueSuffix || 'value',
      type: 'heatmap',
      data: this.dataPoints.map(p => [p.x, p.y, p.value]),
      label: { show: false },
      emphasis: {
        itemStyle: {
          shadowBlur: 8,
          shadowColor: 'rgba(0,0,0,0.4)'
        }
      }
    }];

    const xLabels: string[] = this.xAxisLabels;
    const yLabels: string[] = this.yAxisLabels;

    this.chart.setOption({
      title: this.translatedTitle ? {
        text: this.translatedTitle,
        left: 0,
        top: 0,
        textStyle: { color: textColor, fontSize: 14, fontWeight: 500 }
      } : undefined,
      tooltip: {
        position: 'top',
        formatter: (p: any) => {
          const xL: string = xLabels[p.value[0]] ?? p.value[0];
          const yL: string = yLabels[p.value[1]] ?? p.value[1];
          const v: string = `${p.value[2]}${this.valueSuffix ?? ''}`;
          return `${yL} · ${xL} — <b>${v}</b>`;
        }
      },
      grid: {
        height: '70%',
        top: this.translatedTitle ? 32 : 16,
        left: 60,
        right: 24,
        bottom: 60
      },
      xAxis: {
        type: 'category',
        data: xLabels,
        splitArea: { show: true, areaStyle: { color: ['transparent', splitLineColor] } },
        axisLabel: { color: textColor, fontSize: 10 },
        axisLine: { lineStyle: { color: textColor } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'category',
        data: yLabels,
        splitArea: { show: true, areaStyle: { color: ['transparent', splitLineColor] } },
        axisLabel: { color: textColor, fontSize: 11 },
        axisLine: { lineStyle: { color: textColor } },
        axisTick: { show: false }
      },
      visualMap: {
        min: this.minValue,
        max: this.maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 8,
        itemWidth: 12,
        itemHeight: 110,
        inRange: { color: ramp },
        text: [this.valueLabelHigh, this.valueLabelLow],
        textStyle: { color: textColor, fontSize: 10 }
      },
      series
    }, true);

    this.chart.resize();
  }

  private cssVar(name: string, fallback: string): string {
    const v: string = window.getComputedStyle(document.body)
      .getPropertyValue(name).trim();
    return v || fallback;
  }
}
